// Left sidebar "Files" tab: vault tree plus create/rename/delete/upload actions.
import { clear, formatBytes, h, isMarkdown, joinPath, basename, dirname } from './dom.js';
import { api } from './api.js';
import { confirmDialog, promptDialog, toast } from './modals.js';
import type { TreeNode } from './types.js';
import type { App } from './app.js';

const NOTE_NAME = /\.(md|markdown)$/i;

function normaliseNoteName(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return NOTE_NAME.test(trimmed) ? trimmed : `${trimmed}.md`;
}

export async function createNote(app: App, dir: string): Promise<void> {
  const name = await promptDialog({
    title: 'New note',
    label: dir ? `File name in ${dir}/` : 'File name',
    placeholder: 'my note',
    confirmLabel: 'Create',
  });
  if (name === null) return;
  const path = joinPath(dir, normaliseNoteName(name));
  if (!path) return;
  try {
    const created = await api.writeFile(path, '', null);
    app.noteLocalRev(created.rev);
    await app.vaultChanged();
    await app.open(path);
    toast(`Created ${path}`, { kind: 'success' });
  } catch (error) {
    toast(`Create failed: ${(error as Error).message}`, { kind: 'error' });
  }
}

export async function createFolder(app: App, dir: string): Promise<void> {
  const name = await promptDialog({
    title: 'New folder',
    label: dir ? `Folder name in ${dir}/` : 'Folder name',
    placeholder: 'projects',
    confirmLabel: 'Create',
  });
  if (name === null) return;
  const path = joinPath(dir, name.trim());
  if (!path) return;
  try {
    await api.mkdir(path);
    await app.vaultChanged();
    toast(`Created folder ${path}`, { kind: 'success' });
  } catch (error) {
    toast(`Create failed: ${(error as Error).message}`, { kind: 'error' });
  }
}

export async function renameEntry(app: App, path: string): Promise<void> {
  const name = await promptDialog({
    title: 'Rename',
    label: 'New name',
    value: basename(path),
    confirmLabel: 'Rename',
  });
  if (name === null || name === '') return;
  const target = joinPath(dirname(path), name);
  if (target === path) return;
  try {
    const renamed = await api.rename(path, target);
    app.noteLocalRev(renamed.rev);
    if (app.current === path) app.setCurrentPath(target);
    await app.vaultChanged();
    toast(`Renamed to ${target}`, { kind: 'success' });
  } catch (error) {
    toast(`Rename failed: ${(error as Error).message}`, { kind: 'error' });
  }
}

export async function deleteEntry(app: App, path: string): Promise<void> {
  const ok = await confirmDialog('Delete', `Delete “${path}” from the vault? This is recorded in the revision log.`, 'Delete');
  if (!ok) return;
  try {
    const removed = await api.delete(path);
    app.noteLocalRev(removed.rev);
    await app.vaultChanged();
    if (app.current === path) app.closeCurrent('Deleted');
    toast(`Deleted ${path}`, { kind: 'success' });
  } catch (error) {
    toast(`Delete failed: ${(error as Error).message}`, { kind: 'error' });
  }
}

export async function uploadFiles(app: App, dir: string, files: File[]): Promise<void> {
  let uploaded = 0;
  for (const file of files) {
    const path = joinPath(dir, file.name);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const result = await api.upload(path, bytes);
      app.noteLocalRev(result.rev);
      uploaded += 1;
    } catch (error) {
      toast(`Upload of ${file.name} failed: ${(error as Error).message}`, { kind: 'error' });
    }
  }
  if (uploaded > 0) {
    await app.vaultChanged();
    toast(`Uploaded ${uploaded} file${uploaded === 1 ? '' : 's'}${dir ? ` to ${dir}/` : ''}`, { kind: 'success' });
  }
}

/** Renders the vault tree and keeps expansion state between refreshes. */
export class FileTree {
  readonly el: HTMLElement;
  private readonly list: HTMLElement;
  private readonly app: App;
  private readonly collapsed = new Set<string>();
  private tree: TreeNode | null = null;
  private dropTarget: HTMLElement | null = null;

  constructor(app: App) {
    this.app = app;
    this.list = h('div', { class: 'tree-list' });
    const fileInput = h('input', {
      class: 'hidden-input',
      type: 'file',
      attrs: { multiple: 'multiple' },
    });
    fileInput.addEventListener('change', () => {
      const files = fileInput.files ? Array.from(fileInput.files) : [];
      fileInput.value = '';
      void uploadFiles(this.app, '', files);
    });
    this.el = h('div', { class: 'tree' }, [
      h('div', { class: 'pane-toolbar' }, [
        h('button', { class: 'icon-button', text: '＋', title: 'New note', type: 'button', on: { click: () => void createNote(this.app, '') } }),
        h('button', { class: 'icon-button', text: '🗀', title: 'New folder', type: 'button', on: { click: () => void createFolder(this.app, '') } }),
        h('button', { class: 'icon-button', text: '↥', title: 'Upload attachment', type: 'button', on: { click: () => fileInput.click() } }),
        h('button', { class: 'icon-button', text: '⟳', title: 'Reload tree', type: 'button', on: { click: () => void this.refresh() } }),
        fileInput,
      ]),
      this.list,
    ]);
    this.el.addEventListener('dragover', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    });
    this.el.addEventListener('drop', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      const target = this.dropTarget;
      this.setDropTarget(null);
      if (!target) return;
      const dir = target.dataset['dir'] ?? '';
      void uploadFiles(this.app, dir, Array.from(event.dataTransfer.files));
    });
  }

  async refresh(): Promise<void> {
    const response = await this.app.api.tree();
    this.tree = response.tree;
    this.render();
  }

  private setDropTarget(node: HTMLElement | null): void {
    if (this.dropTarget === node) return;
    this.dropTarget?.classList.remove('drop-target');
    this.dropTarget = node;
    this.dropTarget?.classList.add('drop-target');
  }

  private render(): void {
    clear(this.list);
    if (!this.tree) return;
    for (const child of this.tree.children ?? []) this.list.append(this.renderNode(child, 0));
    if ((this.tree.children ?? []).length === 0) {
      this.list.append(h('p', { class: 'empty-note', text: 'Vault is empty — create a note to start.' }));
    }
  }

  private renderNode(node: TreeNode, depth: number): HTMLElement {
    const isDir = node.type === 'dir';
    if (node.path === '' || (!isDir && !node.path)) return h('div', {});
    const expanded = !this.collapsed.has(node.path);
    const row = h('div', {
      class: `tree-row${this.app.current === node.path ? ' active' : ''}`,
      dataset: { path: node.path, ...(isDir ? { dir: node.path } : {}) },
      attrs: { style: `padding-left:${6 + depth * 12}px` },
      title: node.path,
    }, [
      h('span', { class: 'tree-twisty', text: isDir ? (expanded ? '▾' : '▸') : '' }),
      h('span', { class: 'tree-icon', text: isDir ? '🗀' : isMarkdown(node.path) ? '📝' : '📎' }),
      h('span', { class: 'tree-name', text: node.name }),
      isDir && node.children ? h('span', { class: 'tree-count', text: String(node.children.length) }) : null,
    ]);
    row.addEventListener('click', (event) => {
      if (isDir) {
        if (this.collapsed.has(node.path)) this.collapsed.delete(node.path);
        else this.collapsed.add(node.path);
        this.render();
        return;
      }
      if (event.ctrlKey || event.metaKey) event.preventDefault();
      if (isMarkdown(node.path)) void this.app.open(node.path);
      else window.open(api.rawUrl(node.path, true), '_blank');
    });
    row.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      void this.showMenu(node, event.clientX, event.clientY);
    });
    if (isDir) {
      row.addEventListener('dragover', (event) => {
        if (!event.dataTransfer?.types.includes('Files')) return;
        event.preventDefault();
        event.stopPropagation();
        this.setDropTarget(row);
      });
      row.addEventListener('dragleave', () => this.setDropTarget(null));
    }
    const container = h('div', { class: 'tree-node' }, [row]);
    if (isDir && expanded) {
      for (const child of node.children ?? []) container.append(this.renderNode(child, depth + 1));
    }
    return container;
  }

  private async showMenu(node: TreeNode, x: number, y: number): Promise<void> {
    const menu = h('div', { class: 'context-menu', attrs: { style: `left:${x}px;top:${y}px` } });
    const add = (label: string, run: () => void): void => {
      menu.append(
        h('button', {
          class: 'context-item',
          text: label,
          type: 'button',
          on: {
            click: () => {
              menu.remove();
              run();
            },
          },
        }),
      );
    };
    if (node.type === 'dir') {
      add('New note here', () => void createNote(this.app, node.path));
      add('New folder here', () => void createFolder(this.app, node.path));
      add('Upload attachment here', () => void this.uploadInto(node.path));
    } else {
      add('Open', () => void this.app.open(node.path));
      add('Download', () => window.open(api.rawUrl(node.path, true), '_blank'));
      add('Copy path', () => void navigator.clipboard.writeText(node.path));
    }
    add('Rename…', () => void renameEntry(this.app, node.path));
    add('Delete…', () => void deleteEntry(this.app, node.path));
    document.body.append(menu);
    const close = (event: Event): void => {
      if (event.target instanceof Node && menu.contains(event.target)) return;
      menu.remove();
      document.removeEventListener('mousedown', close);
      document.removeEventListener('blur', close);
    };
    window.setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('blur', close);
    }, 0);
  }

  private async uploadInto(dir: string): Promise<void> {
    const input = h('input', { class: 'hidden-input', type: 'file', attrs: { multiple: 'multiple' } });
    input.addEventListener('change', () => {
      const files = input.files ? Array.from(input.files) : [];
      input.remove();
      void uploadFiles(this.app, dir, files);
    });
    document.body.append(input);
    input.click();
  }
}

export { formatBytes };
